import type { TOptimisticCell } from "../lib/features/optimistic";
import type { CalcResult } from "../lib/domain";
import type { WorkerApi } from "../lib/worker";
import { utcNow } from "../../utils";

type Promisified<T> = T extends (...args: infer A) => infer R ? (...args: A) => Promise<Awaited<R>> : never;

type Calcs = WorkerApi;
export type PendingEdit = { item: TOptimisticCell; id?: number; time: number };
export type AsyncCellEdit = Promisified<Calcs["cellEdit"]>;
export type AsyncGetGrid = Promisified<Calcs["getGrid"]>;

/**
 * Manages optimistic cell edits with batching and debouncing.
 *
 * This class provides optimistic UI updates by maintaining a queue of pending edits
 * that are immediately visible to the user while being asynchronously persisted.
 *
 * Key features:
 * - **Debouncing**: Multiple edit calls are batched together and flushed after a brief delay
 * - **Optimistic updates**: Pending edits overlay the grid results until persisted
 * - **Automatic cleanup**: Edits are removed 500ms after successful persistence
 * - **Most recent wins**: When multiple edits target the same cell, the latest edit is used
 * ```
 */
export class PendingCellEdits {
  /** How long to store edits before reverting to the server value */
  static readonly OPTIMISTIC_EDIT_TIMEOUT = 500;
  /** How long to debounce calls to cellEdit */
  static readonly FLUSH_TIMEOUT = 0;

  constructor(
    private cellEdit: AsyncCellEdit,
    private getGrid: AsyncGetGrid,
  ) {}

  edit = (...edit: TOptimisticCell[]) => {
    for (const e of edit) this.addToPending(e);
    this.scheduleFlushEdits();
  };

  gridWithPending = (
    request: Parameters<Calcs["getGrid"]>,
    result: Awaited<ReturnType<Calcs["getGrid"]>>,
  ): Awaited<ReturnType<Calcs["getGrid"]>> => {
    return result.map((row, ci) =>
      row.map((cell, ri) => {
        const product = request?.[0]?.[ci]?.[0];
        const field = request?.[0]?.[ci]?.[1];
        const offset = request?.[1]?.[ri];
        if (!product || !field || typeof offset === "undefined") return cell;
        return this.cellWithPending(cell, product, field, offset);
      }),
    );
  };

  cellWithPending = (cell: CalcResult, product: string, field: string, offset: number | string): CalcResult => {
    const found = this.getPendingValue(product, field, offset);
    if (found) return found;
    return cell;
  };

  getGridWithPending = async (...args: Parameters<Calcs["getGrid"]>): Promise<ReturnType<Calcs["getGrid"]>> => {
    const result = await this.getGrid(...args);
    return result.map((row, ci) =>
      row.map((cell, ri) => {
        const product = args?.[0]?.[ci]?.[0];
        const field = args?.[0]?.[ci]?.[1];
        const offset = args?.[1]?.[ri];
        if (!product || !field || typeof offset === "undefined") return cell;
        const found = this.getPendingValue(product, field, offset);
        if (found) return found;
        return cell;
      }),
    );
  };

  private flushTimeoutId: NodeJS.Timeout | null = null;
  private scheduleFlushEdits = () => {
    if (this.flushTimeoutId) clearTimeout(this.flushTimeoutId);
    this.flushTimeoutId = setTimeout(() => {
      this.flushTimeoutId = null;
      this.flushNow();
    }, PendingCellEdits.FLUSH_TIMEOUT);
  };

  private flushNow = () => {
    const { edits, done } = this.prepareBatch();
    if (edits.length === 0) return;
    const tsk = this.cellEdit(...edits);
    tsk.finally(() => done());
  };

  private pending: PendingEdit[] = [];
  private addToPending = (edit: TOptimisticCell) => {
    this.pending.push({ item: { ...edit, edited_at: utcNow().valueOf() }, time: Date.now() });
  };

  private batchId = 0;
  private prepareBatch = () => {
    const id = this.batchId++;
    const edits = this.pending.filter((x) => !x.id).map((x) => x.item);
    this.pending = this.pending.map((x) => {
      if (!x.id) return { ...x, id };
      return x;
    });
    const done = async () => {
      await new Promise((resolve) => setTimeout(resolve, PendingCellEdits.OPTIMISTIC_EDIT_TIMEOUT));
      this.pending = this.pending.filter((x) => x.id !== id);
    };
    return { edits, done };
  };

  private getPendingValue = (product: string, field: string, offset: number | string): CalcResult | undefined => {
    const edits = this.pending
      .filter((x) => x.item.product === product && x.item.field === field && x.item.offset === offset)
      .sort((a, b) => b.time - a.time);

    if (typeof edits[0]?.item.result === "number") {
      return { Ok: edits[0].item.result, Err: undefined };
    }
  };
}
